import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { formatDate } from 'utils/formatDate';
import { addDays, daysBetween, fullDay, mondayOf, shortDay, windowEnds } from 'utils/interventionDate';
import { PICKED } from './requestVisuals';

/** Past six rows the grid outgrows a 640 px sheet: the caller switches to pills + a date field. */
export const fitsGrid = (start: string, deadline: string): boolean => daysBetween(mondayOf(start), deadline) < 42;

interface DayGridProps {
  /** First pickable day, already `max(today, earliest)`. */
  start: string;
  deadline: string;
  today: string;
  value: string;
  onPick: (day: string) => void;
}

/** F14 — whole weeks, Monday first, from `start`'s week to `deadline`'s: plain buttons, no calendar lib. */
const DayGrid = ({ start, deadline, today, value, onPick }: DayGridProps) => {
  const { t } = useTranslation();
  const first = mondayOf(start);
  const days = Array.from({ length: Math.ceil((daysBetween(first, deadline) + 1) / 7) * 7 }, (_, i) =>
    addDays(first, i)
  );

  return (
    <div className="flex flex-col gap-2" role="group" aria-labelledby="day-grid-title">
      <p id="day-grid-title" className="text-[13px] font-medium text-zinc-900 dark:text-zinc-100">
        {t('Between {{start}} and {{end}}', windowEnds(start, deadline))}
      </p>
      {/* Bleeds 8 px into the sheet padding: 7 × 46 px at 360 wide, never under 44 (F14). */}
      <div className="-mx-2 grid grid-cols-7 gap-0.5">
        {days.slice(0, 7).map((day) => (
          <span key={day} className="text-center text-[11px] text-zinc-500 dark:text-zinc-400" aria-hidden="true">
            {formatDate(`${day}T00:00:00`, { weekday: 'short' })}
          </span>
        ))}
        {days.map((day) => {
          const out = day < start || day > deadline;
          const picked = day === value;
          const isToday = day === today;
          return (
            <button
              key={day}
              type="button"
              disabled={out}
              aria-pressed={picked}
              aria-label={isToday ? `${fullDay(day)} · ${t('Today')}` : fullDay(day)}
              onClick={() => onPick(day)}
              className={cn(
                'relative flex h-11 min-w-0 items-center justify-center rounded-md border text-[14px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                out
                  ? 'cursor-not-allowed border-transparent text-zinc-300 line-through dark:text-zinc-600'
                  : picked
                    ? PICKED
                    : 'border-zinc-200 text-zinc-700 dark:border-zinc-800 dark:text-zinc-300',
                isToday && 'font-semibold'
              )}
            >
              {Number(day.slice(8))}
              {isToday && (
                <span className="absolute bottom-0.5 text-[9px] font-medium leading-none text-sky-600 dark:text-sky-400" aria-hidden="true">
                  {t('Today (short)')}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* `start` is later than today only when the window's own start is (F14). */}
      {start > today && (
        <p className="text-[12.5px] text-zinc-500 dark:text-zinc-400">
          {/* « 27 sept. » already ends the sentence: never « sept.. » (fr abbreviates with a dot, en/es do not). */}
          {t('No visit before {{start}}. Crossed-out days are not clickable.', { start: shortDay(start).replace(/\.$/, '') })}
        </p>
      )}
    </div>
  );
};

export default DayGrid;
